import type { VersionInfo, Versions } from '../lib/types'
import { Select } from './Select'

const RECENT = 6

/**
 * A version select grouped the way planners think: estimates, versions that follow the cycle
 * (budget of record, prior forecast), frozen versions by the cycle that froze them (newest first,
 * older ones folded into their own group), then scenarios.
 */
export function VersionSelect({
  label,
  value,
  versions,
  cycles = [],
  onChange,
  allowNone = false,
  exclude,
}: {
  label: string
  value: string | null
  versions: readonly VersionInfo[]
  cycles?: Versions['cycles']
  onChange: (key: string | null) => void
  allowNone?: boolean
  exclude?: string | null
}) {
  const groups: [string, VersionInfo[]][] = []
  const push = (g: string, v: VersionInfo) => {
    const found = groups.find(([n]) => n === g)
    if (found) found[1].push(v)
    else groups.push([g, [v]])
  }
  const shown = versions.filter(v => v.key !== exclude)
  for (const v of shown) if (v.kind === 'working' || v.kind === 'actual' || v.kind === 'composite') push('Estimates', v)
  // latest:<cycle> stays usable in config, but the picker skips it when prior or budget already names the same version
  const named = new Set(shown.filter(v => v.key === 'prior' || v.key === 'budget').map(v => v.target))
  for (const v of shown)
    if (v.source === 'relative' && (!v.key.startsWith('latest:') || !named.has(v.target) || v.key === value)) push('Follows the cycle', v)
  const snaps = shown.filter(v => v.kind === 'snapshot' && v.source !== 'relative').reverse()
  const cycleName = (k: string | null) => cycles.find(c => c.key === k)?.name ?? null
  const byCycle = new Map<string, VersionInfo[]>()
  for (const v of snaps) {
    const g = cycleName(v.cycle) ?? 'Frozen versions'
    byCycle.set(g, [...(byCycle.get(g) ?? []), v])
  }
  for (const [g, vs] of byCycle) {
    for (const v of vs.slice(0, RECENT)) push(g, v)
    for (const v of vs.slice(RECENT)) push(`${g} · older`, v)
  }
  for (const v of shown) if (v.kind === 'scenario') push('Scenarios', v)
  const current = versions.find(v => v.key === value)
  const text = (v: VersionInfo) => (v.source === 'relative' ? `${v.relative} · ${v.label}` : v.label)
  return (
    <span className="picker" title={current?.relative ? `${current.relative}: currently ${current.label}` : (current?.note ?? undefined)}>
      <span className="faint">{label}</span>
      <Select
        variant="bare"
        aria-label={label}
        value={value ?? ''}
        onChange={v => onChange(v || null)}
        options={[
          ...(allowNone ? [{ value: '', label: '—' }] : []),
          ...groups.flatMap(([g, vs]) =>
            vs.map(v => ({ value: v.key, label: text(v), group: g, hint: v.frozen && !v.relative ? v.frozen : undefined })),
          ),
        ]}
      />
    </span>
  )
}
